import React, { FC, useCallback } from 'react';
import { Button, Checkbox, Form, Input, Modal } from 'antd';
import OWButton from '../ow_button/OWButton';

const FORM_ID = 'close-purchase-order-form';

export interface ClosePurchaseOrderOptions {
	cancelPurchaseRequestLineItems: boolean;
	cancelReason: string;
}

interface ClosePurchaseOrderModalProps {
	purchaseOrderNumber: string | number;
	openPRLineItemCount: number;
	closing: boolean;
	onCancel: () => void;
	// Called with no options when the PR line items should be left alone, which keeps
	// the close request identical to the plain close.
	onConfirm: (options?: ClosePurchaseOrderOptions) => void;
}

export const ClosePurchaseOrderModal: FC<ClosePurchaseOrderModalProps> = ({
	purchaseOrderNumber,
	openPRLineItemCount,
	closing,
	onCancel,
	onConfirm,
}) => {
	const [form] = Form.useForm();
	const cancelPurchaseRequestLineItems = Form.useWatch('cancelPurchaseRequestLineItems', form);

	const onSubmit = useCallback(
		(values) => {
			if (!values.cancelPurchaseRequestLineItems) {
				onConfirm();
				return;
			}
			onConfirm({
				cancelPurchaseRequestLineItems: true,
				cancelReason: values.cancelReason.trim(),
			});
		},
		[onConfirm]
	);

	const itemsLabel =
		openPRLineItemCount === 1
			? 'the 1 open purchase request item'
			: `the ${openPRLineItemCount} open purchase request items`;

	return (
		<Modal
			visible
			title={`Close PO ${purchaseOrderNumber}?`}
			closable={!closing}
			maskClosable={!closing}
			keyboard={!closing}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={closing}>
					Cancel
				</Button>,
				<OWButton key="submit" type="primary" htmlType="submit" form={FORM_ID} loading={closing}>
					Close PO
				</OWButton>,
			]}
		>
			<Form
				form={form}
				id={FORM_ID}
				layout="vertical"
				requiredMark={false}
				initialValues={{ cancelPurchaseRequestLineItems: false }}
				onFinish={onSubmit}
			>
				{openPRLineItemCount > 0 ? (
					<div>
						<Form.Item
							name="cancelPurchaseRequestLineItems"
							valuePropName="checked"
							className="!mb-0"
						>
							<Checkbox>
								<span className="bodyregular text-character-primary">Also cancel {itemsLabel}</span>
							</Checkbox>
						</Form.Item>
						{cancelPurchaseRequestLineItems && (
							<Form.Item
								name="cancelReason"
								className="!mb-0 mt-s"
								rules={[
									{
										required: true,
										whitespace: true,
										message: 'Please enter a cancellation reason',
									},
								]}
							>
								<Input.TextArea
									rows={3}
									maxLength={1000}
									placeholder="Cancellation reason"
									autoFocus
								/>
							</Form.Item>
						)}
					</div>
				) : (
					<div className="bodyregular text-character-primary">
						Are you sure you want to close this purchase order?
					</div>
				)}
			</Form>
		</Modal>
	);
};
